import { useEffect, useRef, useState } from 'react';
import { appendMaskRect, rectFromPercentages, rectFromPoints, rectToPercentages, replaceMaskRect, type MaskPercentages, type MaskRect } from './ticket-mask';

const initialMaskForm: MaskPercentages = { x: '10', y: '10', width: '30', height: '20' };

export function TicketMasker({ onReady }: { onReady: (blob: Blob | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<ImageBitmap | null>(null);
  const [rects, setRects] = useState<MaskRect[]>([]);
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number } | null>(null);
  const [fileName, setFileName] = useState('');
  const [maskForm, setMaskForm] = useState<MaskPercentages>(initialMaskForm);
  const [maskError, setMaskError] = useState('');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const redraw = (items = rects) => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    context.fillStyle = '#05070a';
    for (const rect of items) context.fillRect(rect.x, rect.y, rect.width, rect.height);
  };

  useEffect(() => { redraw(); }, [rects]);
  useEffect(() => { redraw(); }, [canvasSize]);

  const invalidateConfirmation = () => {
    setConfirmed(false);
    onReady(null);
  };

  const addMask = (rect: MaskRect) => {
    if (!canvasSize) return;
    const next = appendMaskRect(rects, rect, canvasSize);
    setMaskError(next.error ?? '');
    if (next.error) return;
    setRects(next.rects);
    invalidateConfirmation();
  };

  const choose = async (file?: File) => {
    if (!file) return;
    try {
      const image = await createImageBitmap(file);
      imageRef.current = image;
      const scale = Math.min(1, 900 / image.width);
      setRects([]);
      setMaskError('');
      setMaskForm(initialMaskForm);
      setSelectedIndex(null);
      invalidateConfirmation();
      setCanvasSize({ width: Math.round(image.width * scale), height: Math.round(image.height * scale) });
    } catch {
      setCanvasSize(null);
      setMaskError('사진을 열지 못했어요. PNG, JPG, WebP 파일인지 확인해 주세요.');
    }
  };

  const point = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return { x: (event.clientX - box.left) * event.currentTarget.width / box.width, y: (event.clientY - box.top) * event.currentTarget.height / box.height };
  };

  const addKeyboardMask = () => {
    if (!canvasSize) return;
    const rect = rectFromPercentages(maskForm, canvasSize);
    if (!rect) {
      setMaskError('왼쪽과 위쪽, 너비와 높이를 0에서 100 사이의 퍼센트로 넣어 주세요. 영역이 사진 밖으로 나가면 안 돼요.');
      return;
    }
    if (selectedIndex === null) {
      addMask(rect);
      return;
    }
    const next = replaceMaskRect(rects, selectedIndex, rect, canvasSize);
    setMaskError(next.error ?? '');
    if (next.error) return;
    setRects(next.rects);
    invalidateConfirmation();
  };

  const confirm = () => {
    const canvas = canvasRef.current;
    if (!canvas || rects.length === 0) return;
    canvas.toBlob((blob) => {
      if (!blob) {
        setMaskError('가림본을 만들지 못했어요. 다시 시도해 주세요.');
        return;
      }
      setMaskError('');
      setConfirmed(true);
      onReady(blob);
    }, 'image/png', 0.92);
  };

  return <div className="stack">
    <div className="field">
      <span className="field-label">기기에서 티켓 사진 고르기</span>
      <div className="file-picker">
        <input id="ticket-photo" className="file-picker-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => { const file = event.target.files?.[0]; setFileName(file?.name ?? ''); void choose(file); }} />
        <label className="button" htmlFor="ticket-photo">사진 고르기</label>
        <span className="micro">{fileName || '아직 고른 사진이 없어요.'}</span>
      </div>
    </div>
    {canvasSize ? <>
      <p id="mask-instructions" className="muted">이름·전화번호·주문번호·바코드·QR을 모두 덮어 주세요. 사진에서 드래그하거나, 아래 숫자를 키보드로 넣어 가림 영역을 추가할 수 있어요.</p>
      <canvas ref={canvasRef} width={canvasSize.width} height={canvasSize.height} className="mask-canvas" onPointerDown={(event) => { event.currentTarget.setPointerCapture(event.pointerId); setMaskError(''); setStart(point(event)); }} onPointerUp={(event) => { if (!start) return; addMask(rectFromPoints(start, point(event))); setStart(null); }} aria-label="티켓에서 개인정보를 검게 가리는 미리보기" aria-describedby="mask-instructions mask-status" />
      <fieldset className="mask-keyboard-control">
        <legend>{selectedIndex === null ? '키보드로 가림 영역 추가하기' : `가림 영역 ${selectedIndex + 1} 수정하기`}</legend>
        <p className="micro">사진의 왼쪽과 위쪽, 너비와 높이를 퍼센트로 넣어 주세요.</p>
        <div className="mask-coordinate-grid">
          {([['x', '왼쪽'], ['y', '위쪽'], ['width', '너비'], ['height', '높이']] as const).map(([key, label]) => <label className="field" key={key}><span className="field-label">{label} (%)</span><input className="input" type="number" inputMode="decimal" min="0" max="100" step="1" value={maskForm[key]} onChange={(event) => setMaskForm({ ...maskForm, [key]: event.target.value })} /></label>)}
        </div>
        <div className="inline"><button className="button button-secondary" type="button" onClick={addKeyboardMask}>{selectedIndex === null ? '가림 영역 추가하기' : '가림 영역 수정하기'}</button>{selectedIndex !== null ? <button className="button button-ghost" type="button" onClick={() => { setSelectedIndex(null); setMaskForm(initialMaskForm); }}>새 영역 추가로 전환하기</button> : null}</div>
      </fieldset>
      <p id="mask-status" className="mask-status" role="status" aria-live="polite">{rects.length ? `가림 영역 ${rects.length}개를 사진 미리보기에 적용했어요. 확인하면 가림본을 만들어요.` : '아직 가림 영역이 없어요. 사진에서 드래그하거나 키보드 입력으로 추가해 주세요.'}</p>
      {maskError ? <p className="mask-error" role="alert">{maskError}</p> : null}
      {rects.length ? <ol className="mask-list" aria-label="가린 영역 목록">{rects.map((rect, index) => <li key={`${rect.x}-${rect.y}-${rect.width}-${rect.height}-${index}`}><button className="button button-ghost" type="button" onClick={() => { setSelectedIndex(index); setMaskForm(rectToPercentages(rect, canvasSize)); setMaskError(''); }}>가림 영역 {index + 1} 수정하기</button><button className="button button-ghost" type="button" onClick={() => { setRects(rects.filter((_, current) => current !== index)); setSelectedIndex(null); setMaskForm(initialMaskForm); setMaskError(''); invalidateConfirmation(); }}>삭제하기</button></li>)}</ol> : null}
      <div className="button-row">
        <button className="button button-secondary" type="button" onClick={() => { setRects([]); setSelectedIndex(null); setMaskForm(initialMaskForm); setMaskError(''); invalidateConfirmation(); }}>가림 다시 하기</button>
        <button className="button button-primary" type="button" disabled={!rects.length} onClick={confirm}>가린 부분 확인하기</button>
      </div>
      {confirmed ? <p role="status" className="mask-success">가림본을 확인했어요. 원본은 이 기기 밖으로 보내지 않아요.</p> : null}
    </> : null}
  </div>;
}
